/**
 * ContentString sanitisation (ADR-009 §2.1 rule 4). Document-authored text is
 * data, never instructions: C0 and C1 controls (U+0000–U+001F, U+007F–U+009F)
 * and the bidi embedding/override/isolate characters (U+202A–U+202E,
 * U+2066–U+2069) are replaced by literal `\uXXXX` text so they cannot reorder
 * or hide what a reader sees, and the value is capped.
 */

// eslint-disable-next-line no-control-regex
const UNSAFE = /[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/gu;

function escapeUnsafe(value: string): string {
  return value.replace(UNSAFE, (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
}

/** Truncates to at most `maxLength` UTF-16 units of *source* text without splitting a surrogate pair. */
function truncateSource(value: string, maxLength: number): { value: string; truncated: boolean } {
  if (value.length <= maxLength) return { value, truncated: false };
  let end = Math.max(0, maxLength);
  const code = value.charCodeAt(end - 1);
  if (end > 0 && code >= 0xd800 && code <= 0xdbff) end -= 1;
  return { value: value.slice(0, end), truncated: true };
}

export interface ContentStringResult { value: string; truncated: boolean }

/** Escape and cap. `maxLength` applies to the source text before escaping. */
export function contentString(value: string, maxLength = Infinity): ContentStringResult {
  const capped = truncateSource(value, maxLength);
  return { value: escapeUnsafe(capped.value), truncated: capped.truncated };
}

/** Escaped, capped value with a trailing ellipsis when truncated (names, labels). */
export function contentLabel(value: string, maxLength: number): string {
  const result = contentString(value, maxLength);
  return result.truncated ? `${result.value}…` : result.value;
}

export const CONTENT_LIMITS = Object.freeze({ name: 200, text: 2_000, prompt: 2_000, label: 80, key: 128 });
